import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Calendar, Car, CheckCircle2, Loader2, XCircle, Clock, ChevronLeft, Wrench, ShieldCheck } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL ?? "";

async function publicApi(path, { method = "GET", body } = {}) {
  const res = await fetch(`${BASE}/api${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = typeof data?.detail === "string" ? data.detail : data?.detail?.message || `Error ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.code = data?.detail?.code;
    throw err;
  }
  return data;
}

const money = (n) => (typeof n === "number" ? `£${n.toFixed(2)}` : "");

// Returns the next N selectable dates (YYYY-MM-DD) within the advance window.
function upcomingDates(advanceDays) {
  const out = [];
  const today = new Date();
  const max = Math.min(advanceDays || 30, 60);
  for (let i = 0; i <= max; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    out.push(d);
  }
  return out;
}

const fmtDate = (d) =>
  d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const isoDate = (d) => {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};

export default function BookNow() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";

  const [garage, setGarage] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(null); // Date object
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ vrm: "", name: "", email: "", phone: "", notes: "", company: "" });
  const [veh, setVeh] = useState(null); // looked-up/verified vehicle details
  const [looking, setLooking] = useState(false);
  const [lookupMsg, setLookupMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [formErr, setFormErr] = useState("");

  useEffect(() => {
    publicApi(`/public/book/${token}`)
      .then(setGarage)
      .catch((e) => setLoadErr(e.message || "This booking page is not available."));
  }, [token]);

  const loadSlots = useCallback(async (svc, d) => {
    if (!svc || !d) return;
    setSlotsLoading(true);
    setSlots([]);
    setTime("");
    try {
      const r = await publicApi(`/public/book/${token}/availability?service_id=${svc.id}&date=${isoDate(d)}`);
      setSlots(r.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [token]);

  const findVehicle = async () => {
    const vrm = (form.vrm || "").trim();
    if (!vrm) return;
    setLooking(true); setLookupMsg(""); setVeh(null);
    try {
      const r = await publicApi(`/public/book/${token}/vehicle-lookup?vrm=${encodeURIComponent(vrm)}`);
      setVeh(r);
      setLookupMsg("");
    } catch (e) {
      setLookupMsg(e.message || "Lookup unavailable — please enter details manually.");
    } finally { setLooking(false); }
  };

  const submit = async () => {
    setFormErr("");
    setSubmitting(true);
    try {
      const r = await publicApi(`/public/book/${token}`, {
        method: "POST",
        body: {
          service_id: service.id, date: isoDate(date), time, ...form,
          make: veh?.make || null, model: veh?.model || null, colour: veh?.colour || null,
          year: veh?.year ? Number(veh.year) : null,
        },
      });
      setResult(r);
      setStep(5);
    } catch (e) {
      setFormErr(e.message);
      if (e.code === "slot_full" || e.code === "invalid_slot" || e.code === "too_soon") {
        setStep(2);
        loadSlots(service, date);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadErr) {
    return (
      <Shell embed={embed}>
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-500" />
          <p className="mt-4 text-sm text-slate-600" data-testid="booknow-error">{loadErr}</p>
        </div>
      </Shell>
    );
  }
  if (!garage) {
    return <Shell embed={embed}><Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" /></Shell>;
  }

  return (
    <Shell embed={embed}>
      <div data-testid="booknow-page">
        {/* Header */}
        <div className="mb-5 flex items-center gap-3 border-b border-slate-100 pb-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white"><Wrench className="h-5 w-5" /></div>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-extrabold text-slate-900" data-testid="booknow-garage-name">{garage.name}</h1>
            <p className="truncate text-xs text-slate-500">{garage.address || "Book your appointment online"}</p>
          </div>
        </div>

        {step < 5 && <Steps step={step} />}

        {/* Step 1: service */}
        {step === 1 && (
          <div className="space-y-3" data-testid="booknow-step-service">
            <p className="text-sm font-bold text-slate-700">Choose a service</p>
            {(garage.services || []).length === 0 && <p className="text-sm text-slate-500">No services are currently available.</p>}
            {(garage.services || []).map((s) => (
              <button key={s.id} data-testid={`booknow-service-${s.id}`}
                onClick={() => { setService(s); setDate(null); setSlots([]); setTime(""); setStep(2); }}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-blue-500 hover:bg-blue-50/40">
                <div>
                  <p className="text-sm font-bold text-slate-900">{s.name}</p>
                  {s.description && <p className="mt-0.5 text-xs text-slate-500">{s.description}</p>}
                  <p className="mt-1 text-xs font-bold text-blue-700" data-testid={`booknow-price-${s.id}`}>{s.display_price}{s.pricing_mode === "from" ? "" : ""}</p>
                  <p className="mt-0.5 text-xs text-slate-400">{s.duration_minutes} min</p>
                </div>
                <ChevronLeft className="h-4 w-4 rotate-180 text-slate-400" />
              </button>
            ))}
          </div>
        )}

        {/* Step 2: date + time */}
        {step === 2 && (
          <div data-testid="booknow-step-datetime">
            <BackBtn onClick={() => setStep(1)} label={service?.name} />
            <p className="mb-2 mt-3 text-sm font-bold text-slate-700">Pick a date</p>
            <div className="flex gap-2 overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: "touch" }}>
              {upcomingDates(garage.advance_window_days).map((d) => {
                const sel = date && isoDate(d) === isoDate(date);
                return (
                  <button key={isoDate(d)} data-testid={`booknow-date-${isoDate(d)}`}
                    onClick={() => { setDate(d); loadSlots(service, d); }}
                    className={`flex shrink-0 flex-col items-center rounded-lg border px-3 py-2 text-xs ${sel ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-400"}`}>
                    <span className="font-bold">{fmtDate(d)}</span>
                  </button>
                );
              })}
            </div>

            {date && (
              <>
                <p className="mb-2 mt-4 text-sm font-bold text-slate-700">Pick a time</p>
                {slotsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                ) : slots.length === 0 ? (
                  <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="booknow-no-slots">No available times on this day — please choose another date.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {slots.map((t) => (
                      <button key={t} data-testid={`booknow-slot-${t}`}
                        onClick={() => { setTime(t); setStep(3); }}
                        className="rounded-lg border border-slate-200 bg-white py-2 text-sm font-semibold text-slate-700 hover:border-blue-600 hover:bg-blue-50">
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 3: details */}
        {step === 3 && (
          <div data-testid="booknow-step-details">
            <BackBtn onClick={() => setStep(2)} label={`${fmtDate(date)} · ${time}`} />
            <div className="mt-3 space-y-3">
              <div>
                <span className="mb-1 block text-xs font-bold text-slate-600">Vehicle registration</span>
                <div className="flex gap-2">
                  <input data-testid="booknow-vrm" value={form.vrm} onChange={(e) => { setForm({ ...form, vrm: e.target.value.toUpperCase() }); setVeh(null); setLookupMsg(""); }} placeholder="AB12 CDE"
                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm uppercase outline-none focus:border-blue-600" />
                  <button type="button" onClick={findVehicle} disabled={looking || !form.vrm.trim()} data-testid="booknow-find-vehicle"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2.5 text-sm font-bold text-white disabled:opacity-50">
                    {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Car className="h-4 w-4" />}Find
                  </button>
                </div>
                {veh && (
                  <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="booknow-vehicle-info">
                    <p className="font-bold text-emerald-900">{[veh.make, veh.model].filter(Boolean).join(" ") || "Vehicle found"}</p>
                    <p className="text-xs text-emerald-700">{[veh.colour, veh.year, veh.fuel_type].filter(Boolean).join(" · ")}</p>
                    <p className="mt-1 text-[11px] text-emerald-600">Please check these match your vehicle.</p>
                  </div>
                )}
                {lookupMsg && <p className="mt-2 text-xs text-amber-700" data-testid="booknow-lookup-msg">{lookupMsg} You can continue and enter details manually.</p>}
              </div>
              <Input label="Your name" testId="booknow-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Input label="Email" testId="booknow-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Input label="Telephone" testId="booknow-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-slate-600">Notes (optional)</span>
                <textarea data-testid="booknow-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-600" />
              </label>
              {/* Honeypot (hidden from users) */}
              <input tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} aria-hidden="true" />
              <button data-testid="booknow-review-btn" disabled={!form.vrm || !form.name || !form.email || !form.phone}
                onClick={() => setStep(4)}
                className="w-full rounded-lg bg-blue-600 py-3 text-sm font-extrabold text-white disabled:opacity-50">
                Review booking
              </button>
            </div>
          </div>
        )}

        {/* Step 4: review */}
        {step === 4 && (
          <div data-testid="booknow-step-review">
            <BackBtn onClick={() => setStep(3)} label="Your details" />
            <div className="mt-3 space-y-2.5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <Row icon={Wrench} label={service.name} />
              <div className="flex items-center gap-2.5 text-slate-800"><span className="h-4 w-4" /><span className="text-sm font-bold text-blue-700" data-testid="booknow-review-price">{service.display_price}</span></div>
              <Row icon={Calendar} label={`${fmtDate(date)} at ${time}`} />
              <Row icon={Car} label={[veh?.make, veh?.model].filter(Boolean).join(" ") ? `${form.vrm} · ${[veh.make, veh.model].filter(Boolean).join(" ")}` : form.vrm} />
              <div className="border-t border-slate-200 pt-2 text-slate-600">
                <p className="font-semibold text-slate-800">{form.name}</p>
                <p>{form.email} · {form.phone}</p>
                {form.notes && <p className="mt-1 italic">“{form.notes}”</p>}
              </div>
            </div>
            {garage.privacy_note && (
              <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{garage.privacy_note}
              </p>
            )}
            {formErr && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="booknow-submit-error">{formErr}</p>}
            <button data-testid="booknow-submit-btn" disabled={submitting} onClick={submit}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 py-3 text-sm font-extrabold text-white disabled:opacity-60">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {garage.mode === "auto" ? "Confirm booking" : "Submit request"}
            </button>
          </div>
        )}

        {/* Step 5: done */}
        {step === 5 && result && (
          <div className="text-center" data-testid="booknow-step-done">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <h2 className="mt-3 text-lg font-extrabold text-slate-900">
              {result.status === "booked" ? "Booking confirmed" : "Request received"}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {result.status === "booked"
                ? "Your appointment is confirmed. A confirmation email is on its way."
                : "The garage will review your request and confirm by email shortly."}
            </p>
            <div className="mx-auto mt-5 max-w-xs rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Reference</p>
              <p className="text-xl font-extrabold tracking-wide text-slate-900" data-testid="booknow-reference">{result.reference}</p>
              <p className="mt-2 text-slate-600">{result.service || service?.name}</p>
              {result.price_display && <p className="text-sm font-bold text-blue-700" data-testid="booknow-done-price">{result.price_display}</p>}
              <p className="text-slate-600" data-testid="booknow-when">{date && time ? `${fmtDate(date)} at ${time}` : (result.when || "").slice(11, 16)}</p>
            </div>
            {garage.phone && <p className="mt-5 text-xs text-slate-500">Questions? Call {garage.phone}</p>}
          </div>
        )}

        <p className="mt-8 text-center text-[11px] text-slate-400">Powered by Pro Garages</p>
      </div>
    </Shell>
  );
}

function Shell({ children, embed }) {
  return (
    <div className={`min-h-screen bg-slate-100 ${embed ? "p-0" : "px-4 py-8"} flex items-start justify-center`}>
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">{children}</div>
    </div>
  );
}

function Steps({ step }) {
  const labels = ["Service", "Date & time", "Details", "Review"];
  return (
    <div className="mb-5 flex items-center gap-1.5">
      {labels.map((l, i) => (
        <div key={l} className={`h-1.5 flex-1 rounded-full ${i + 1 <= step ? "bg-blue-600" : "bg-slate-200"}`} title={l} />
      ))}
    </div>
  );
}

function BackBtn({ onClick, label }) {
  return (
    <button onClick={onClick} data-testid="booknow-back" className="flex items-center gap-1 text-xs font-semibold text-blue-600">
      <ChevronLeft className="h-4 w-4" /> {label || "Back"}
    </button>
  );
}

function Input({ label, value, onChange, testId, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold text-slate-600">{label}</span>
      <input type={type} value={value} data-testid={testId} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-600" />
    </label>
  );
}

function Row({ icon: Icon, label }) {
  return <div className="flex items-center gap-2.5 text-slate-800"><Icon className="h-4 w-4 text-slate-400" /><span className="font-semibold">{label}</span></div>;
}
